import { FC, ReactNode } from 'react';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { CHAT_TOKENS } from '../constants';

/**
 * `gradient` — brand wash only, used behind an open thread.
 * `image` — the marketing photo behind the new-chat hero, kept from the
 * previous design because that screen is the product's front door.
 */
export type ChatBackdrop = 'gradient' | 'image';

export interface ChatSurfaceProps {
  /** Pinned above the scrolling content. Inside the window when `framed`. */
  header?: ReactNode;
  children: ReactNode;
  backdrop?: ChatBackdrop;
  /**
   * Wrap the content in the opaque chat window. On for a thread, where the
   * reading column needs a settled surface; off for the hero, which is meant to
   * sit directly on its backdrop.
   */
  framed?: boolean;
}

/** Brand sweep — blue → neutral → green, the same order as the Komplyance mark. */
const GRADIENT = 'linear-gradient(155deg,#E7F0FC 0%,#EFEFF2 46%,#E8F6EF 100%)';
/**
 * Same sweep, translucent — laid over the photo so the image reads at roughly
 * 10–18% and the hero copy keeps its contrast.
 */
const GRADIENT_WASH =
  'linear-gradient(155deg,rgba(231,240,252,.9) 0%,rgba(239,239,242,.82) 46%,rgba(232,246,239,.9) 100%)';

export const ChatSurface: FC<ChatSurfaceProps> = observer(
  ({ header, children, backdrop = 'gradient', framed = true }) => {
    const isImage = backdrop === 'image';

    const content = (
      <>
        {/* The header owns its own divider so the rule can be inset to the
            content gutter instead of spanning the window edge to edge. */}
        {header && <Box sx={{ flexShrink: 0 }}>{header}</Box>}
        {children}
      </>
    );

    return (
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 4,
          p: { xs: 0.75, md: 1.25 },
          backgroundColor: 'background.default',
          ...(isImage
            ? {
                // Both layers live in ::before — it precedes the children in tree
                // order, so the wash stays behind the content instead of covering
                // it. The wash's own alpha is what dims the photo.
                border: '1px solid #E7E7EC',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  pointerEvents: 'none',
                  backgroundImage: `${GRADIENT_WASH}, url("/login-background.jpg")`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                },
              }
            : { background: GRADIENT }),
        }}
      >
        {framed ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              // Above the backdrop layers painted by the wrapper's ::before.
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: '20px',
              border: `1px solid ${CHAT_TOKENS.hairline}`,
              backgroundColor: 'common.white',
              boxShadow: '0 1px 2px rgba(16,24,40,.04), 0 14px 36px rgba(16,24,40,.07)',
            }}
          >
            {content}
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              // Above the backdrop layers painted by the wrapper's ::before.
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {content}
          </Box>
        )}
      </Box>
    );
  },
);
